import { createFileRoute } from "@tanstack/react-router"
import { useState, useMemo } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import { SectionHeading, Paragraph, FeatureGrid, TierCards, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-bold-italic-text")!

export const Route = createFileRoute("/tools/instagram-bold-italic-text")({
  head: () => ({
    meta: [
      ...createLLMOMeta({ title: tool.seo.title, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-bold-italic-text" }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-bold-italic-text" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-bold-italic-text" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-bold-italic-text" },
      ]}),
      createHowToSchema({ name: "How to Create Bold & Italic Text for Instagram", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: BoldItalicTextPage,
})

// --- Unicode conversion helpers ---

function convertChar(char: string, upperStart: number, lowerStart: number, digitStart?: number): string {
  const c = char.charCodeAt(0)
  if (c >= 65 && c <= 90) return String.fromCodePoint(upperStart + (c - 65))
  if (c >= 97 && c <= 122) return String.fromCodePoint(lowerStart + (c - 97))
  if (digitStart && c >= 48 && c <= 57) return String.fromCodePoint(digitStart + (c - 48))
  return char
}

function convertText(text: string, upperStart: number, lowerStart: number, digitStart?: number): string {
  return Array.from(text).map((ch) => convertChar(ch, upperStart, lowerStart, digitStart)).join("")
}

// Italic lowercase 'h' is a special case in Unicode (U+210E instead of the expected position)
function convertItalic(text: string): string {
  return Array.from(text).map((ch) => {
    if (ch === "h") return String.fromCodePoint(0x210E)
    return convertChar(ch, 0x1D434, 0x1D44E)
  }).join("")
}

type TextStyle = {
  name: string
  convert: (text: string) => string
}

const TEXT_STYLES: TextStyle[] = [
  { name: "Bold", convert: (t) => convertText(t, 0x1D400, 0x1D41A, 0x1D7CE) },
  { name: "Italic", convert: convertItalic },
  { name: "Bold Italic", convert: (t) => convertText(t, 0x1D468, 0x1D482) },
]

const MAX_CHARS = 500

function BoldItalicTextPage() {
  const [text, setText] = useState("")
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)

  const results = useMemo(() => {
    if (!text.trim()) return []
    return TEXT_STYLES.map((style) => ({
      name: style.name,
      converted: style.convert(text),
    }))
  }, [text])

  const copyToClipboard = async (value: string, index: number) => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      const textarea = document.createElement("textarea")
      textarea.value = value
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand("copy")
      document.body.removeChild(textarea)
    }
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
  }

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          <ContentSection>
            <SectionHeading>How Bold and Italic Text Works on Instagram</SectionHeading>
            <Paragraph className="mb-4">
              If you have ever tried to bold a word in an Instagram caption, you have probably noticed there is no formatting toolbar. Instagram does not support native rich-text formatting anywhere in the app. There is no bold button in captions, no italic option in bios, and no underline feature in comments or DMs. Every piece of text you publish is treated as plain, unstyled characters. This is a deliberate design choice by Instagram to keep content uniform across the platform, but it creates a challenge for creators who want their words to stand out.
            </Paragraph>
            <Paragraph className="mb-4">
              The workaround relies on a feature of the Unicode standard called Mathematical Alphanumeric Symbols. Unicode, the system that defines how text is represented digitally, includes thousands of characters beyond the basic Latin alphabet. Among these are complete sets of letters that visually resemble bold, italic, and bold-italic versions of the standard A-Z and a-z characters. For example, the "mathematical bold capital A" (U+1D400) looks identical to a bold "A" you would see in a word processor, but it is technically a completely different character with its own unique code point.
            </Paragraph>
            <Paragraph className="mb-4">
              This distinction is critical. When you use this tool to generate bold text, you are not applying a formatting style that can be stripped away. You are replacing each standard letter with a separate Unicode character that happens to look bold. Because these are real, standalone characters rather than styling instructions, they survive anywhere plain text is accepted. They display correctly on iOS, Android, desktop browsers, and even in email clients. Instagram has no reason to strip them because, from a technical perspective, they are just ordinary text characters like any other letter or emoji.
            </Paragraph>
            <Paragraph>
              The same principle applies to italic and bold-italic text. Each style maps to its own range within the Unicode specification, giving you three distinct visual treatments without relying on any platform-specific formatting feature. The result is text that looks formatted but behaves exactly like regular characters when it comes to copying, pasting, and publishing.
            </Paragraph>
          </ContentSection>
          <ContentSection>
            <SectionHeading>When to Use Bold and Italic Text</SectionHeading>
            <FeatureGrid items={[
              { title: "Bio Headlines and Key Phrases", description: "Your Instagram bio is limited to 150 characters, so every word needs to pull its weight. Using bold text for your name, job title, or core value proposition immediately creates a visual hierarchy that separates you from the millions of accounts using plain text. A bold tagline like \"Helping creators grow\" catches the eye before a visitor even reads the rest of your bio." },
              { title: "Emphasizing Important Words in Captions", description: "Long captions can lose readers quickly on mobile screens. Bolding one or two key phrases per paragraph gives scanners an anchor point and helps communicate your main message even if someone does not read every word. Think of bold text as the headlines within your caption." },
              { title: "Drawing Attention to Calls-to-Action", description: "A bold \"Link in bio\" or \"Comment YES below\" is significantly harder to scroll past than the same phrase in regular text. When your caption builds toward a specific action, bold formatting on the CTA line creates a visual break that signals \"this is the important part.\"" },
              { title: "Making Testimonials Stand Out", description: "If you share customer quotes or user feedback in your captions, italic text provides an instant visual cue that you are quoting someone else. This mimics the convention readers are familiar with from books and articles, making your social proof feel more polished and credible." },
              { title: "Section Headers in Long Captions", description: "Carousel posts and educational content often require captions with multiple distinct sections. Bold text at the start of each section acts as a mini headline, letting followers jump to the part they care about most. This dramatically improves readability on content that runs several hundred words." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Bold vs. Italic vs. Bold Italic — When to Use Which</SectionHeading>
            <Paragraph className="mb-4">
              Each formatting style communicates a different tone, and choosing the right one makes your text feel intentional rather than random.
            </Paragraph>
            <TierCards tiers={[
              { label: "Bold", range: "Authority and Emphasis", color: "blue", description: "Bold text feels strong, confident, and direct. Use it for headlines, key takeaways, brand names, and calls-to-action. For example, bolding \"Free shipping ends tonight\" in a product caption creates urgency without needing exclamation marks or all-caps." },
              { label: "Italic", range: "Quotes, Thoughts, and Nuance", color: "green", description: "Italic text feels softer and more reflective. It is ideal for direct quotes, internal thoughts, book or song titles, and words you want to highlight with subtlety. Writing \"She told me it changed everything\" in italics signals a quote and adds emotional weight that bold would overpower." },
              { label: "Bold Italic", range: "Maximum Impact", color: "yellow", description: "Bold italic combines the weight of bold with the expressiveness of italic. Reserve it for moments that deserve the most attention, such as a single-line reveal, a transformative result, or the emotional climax of a story caption. Overusing bold italic dilutes its power, so treat it as your strongest formatting tool and deploy it sparingly." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Accessibility Considerations</SectionHeading>
            <Paragraph className="mb-4">
              While Unicode-styled text is a powerful visual tool, it comes with trade-offs that responsible creators should understand. Screen readers and assistive technologies do not always handle Mathematical Alphanumeric Symbols gracefully. Some screen readers will read each Unicode bold character by its full technical name (for example, "mathematical bold capital H") instead of simply reading the letter, which makes the content nearly incomprehensible for visually impaired users. Others may skip the characters entirely or read them as unknown symbols.
            </Paragraph>
            <Paragraph className="mb-4">
              For this reason, you should never use Unicode-formatted text for critical information that all users need to understand. If your caption contains an important instruction, a discount code, or a time-sensitive announcement, keep that text in standard characters. Reserve bold and italic formatting for decorative emphasis where the meaning is still clear from the surrounding plain text.
            </Paragraph>
            <Paragraph>
              It is also wise to mix formatted and regular text rather than converting entire paragraphs. A caption where only the headline or one key phrase is bolded remains fully accessible because the screen reader can convey the surrounding context in plain language. Finally, test your formatted text on multiple devices before committing to a style. The way Unicode characters render can vary slightly between iOS, Android, and different app versions, so a quick check ensures your audience sees what you intended.
            </Paragraph>
          </ContentSection>
          <ContentSection>
            <SectionHeading>Tips for Using Formatted Text Effectively</SectionHeading>
            <TipList tips={[
              { title: "Less is more.", description: "The power of bold and italic text comes from contrast. If every other word is formatted, nothing stands out. Limit yourself to one or two formatted phrases per caption to maintain visual impact and keep the text easy to scan." },
              { title: "Bold your key phrases, not your paragraphs.", description: "A single bolded sentence at the top of a caption acts as a headline and draws the reader in. A fully bolded paragraph just looks heavy and discourages reading. Think of bold as a spotlight, not a floodlight." },
              { title: "Pair formatted text with line breaks.", description: "Bold and italic text has the most impact when surrounded by whitespace. Place your formatted phrase on its own line or after a line break so it has room to breathe and immediately catches the eye as followers scroll." },
              { title: "Never use Unicode characters in hashtags.", description: "Instagram's hashtag system only recognizes standard ASCII characters. A hashtag written in Unicode bold letters will not link to any topic page and will not help your discoverability. Always switch back to plain text before typing your hashtags." },
              { title: "Test readability on a real device.", description: "What looks great in a desktop browser may render differently on a small phone screen. Before publishing, paste your formatted text into a draft Instagram post and preview it on the device most of your audience uses. Pay attention to line wrapping and character spacing to ensure everything reads smoothly." },
            ]} />
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        {/* Input Card */}
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
            Enter your text
          </label>
          <textarea
            value={text}
            onChange={(e) => {
              if (e.target.value.length <= MAX_CHARS) setText(e.target.value)
            }}
            placeholder="Type something like: Check out my new post!"
            rows={3}
            className="w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 px-4 py-3 text-sm text-neutral-900 dark:text-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-accent/40 focus:border-brand-accent transition-all resize-none"
          />
          <p className="mt-1.5 text-xs text-neutral-400 dark:text-neutral-500 text-right">
            {text.length}/{MAX_CHARS}
          </p>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="mt-6 space-y-3">
            {results.map((result, index) => (
              <div
                key={result.name}
                className="flex items-center gap-3 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 px-4 py-3 shadow-sm"
              >
                <span className="shrink-0 w-28 sm:w-32 text-xs sm:text-sm font-semibold text-neutral-500 dark:text-neutral-400">
                  {result.name}
                </span>
                <p className="flex-1 min-w-0 text-sm sm:text-base text-neutral-900 dark:text-white break-all leading-relaxed">
                  {result.converted}
                </p>
                <button
                  onClick={() => copyToClipboard(result.converted, index)}
                  className={`shrink-0 h-8 px-3 rounded-lg text-xs font-semibold transition-all duration-300 ${
                    copiedIndex === index
                      ? "bg-green-500 text-white"
                      : "bg-brand-accent text-white shadow-[0_2px_8px_rgba(244,63,94,0.15)] hover:shadow-[0_4px_14px_rgba(244,63,94,0.15)] hover:-translate-y-0.5"
                  }`}
                >
                  {copiedIndex === index ? "Copied!" : "Copy"}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {text.trim() === "" && (
          <div className="mt-6 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-700 p-8 text-center">
            <p className="text-sm text-neutral-400 dark:text-neutral-500">
              Start typing above to see your text in Bold, Italic, and Bold Italic
            </p>
          </div>
        )}
      </div>
    </ToolPageLayout>
  )
}
